import React, { useEffect, useMemo, useRef, useState } from 'react';

import './accommodationForm.css';

// =========================================================
// SECTIONS
// =========================================================

import BasicInformation from './basic/BasicInformation';
import LocationSection from './location/LocationSection';
import AmenitiesSection from './amenities/AmenitiesSection';
import PricingSection from './pricing/PricingSection';
import PoliciesSection from './policies/PoliciesSection';
import ServicesSection from './services/ServicesSection';
import MediaSection from './media/MediaSection';
import AccommodationUnits from './units/AccommodationUnits';
import AdditionalInformation from './additional/AdditionalInformation';
import ReviewSubmit from './review/ReviewSubmit';

// =========================================================
// CONFIG / VALIDATION
// (split out of this file - it used to hold the full wizard
// shape, steps, permissions and validation logic inline,
// pushing this component past 1700 lines)
// =========================================================

import {
    initialFormData,
    steps,
    permissions,
    normalizeMedia,
    cleanFileObject,
    isObject,
    toNumber
} from './accommodationFormConfig';

import { validateStep as validateStepData, validateAllSteps as validateAllStepsData } from './accommodationValidation';

// =========================================================
// API / UTILS
// =========================================================

import { toast } from 'react-toastify';

import buildAccommodationPayload, { buildFormDataFromAccommodation } from '../../utils/accommodationPayload';

import uploadAccommodationMedia from '../../utils/accommodationMediaUpload';

import { orgCreateAccommodation, orgUpdateAccommodation } from '../../api/orgAccommodation';

/*
 * Support (role='support') doesn't use the org-only
 * /org/accommodation/* endpoints (those 403 for anything but
 * an organizer's own token). Support instead goes through the
 * generic org-pack admin endpoints with type='camp', which is
 * how accommodation was already wired into the support pack
 * approval/edit flow on the backend.
 */
import { supTripCreateFun, supTripEdit } from '../../api/supportAdmin';

// =========================================================
// CONSTANTS
// =========================================================

const DRAFT_KEY = 'trvto_accommodation_draft';

// =========================================================
// COMPONENT
// =========================================================

const AccommodationForm = ({
    mode = 'create',
    role = 'organizer',
    accommodationId = null,
    initialData = null,
    orgId = null,
    onSuccess = null
}) => {
    // =====================================================
    // STATE
    // =====================================================

    const [formData, setFormData] = useState(initialFormData);

    const [currentStep, setCurrentStep] = useState(0);

    const [completedSteps, setCompletedSteps] = useState([]);

    const [errors, setErrors] = useState({});

    const [saving, setSaving] = useState(false);

    const draftInitialized = useRef(false);

    // =====================================================
    // PERMISSIONS
    // =====================================================

    const currentPermissions = useMemo(() => {
        return permissions[role] || permissions.organizer;
    }, [role]);

    const canEdit = (section) => {
        return currentPermissions[section] === true;
    };

    // =====================================================
    // INITIAL DATA / EDIT MODE
    // =====================================================

    useEffect(() => {
        if (!initialData) {
            return;
        }

        /*
         * initialData is the RAW record fetched from the
         * backend (GET /org/accommodation/:id) - it's shaped
         * like the Mongoose schema, not like this form's
         * internal state (grouped amenities, per-unit pricing
         * as an object instead of an array, house rules as a
         * tag list instead of booleans, etc).
         *
         * buildFormDataFromAccommodation() is the reverse of
         * buildAccommodationPayload() and does that conversion
         * in one place, so every step sees the shape it expects
         * instead of the raw backend document.
         */
        const mergedData = buildFormDataFromAccommodation(initialData);

        /*
         * DUPLICATE:
         *
         * mode='create' + initialData present only happens for
         * duplicate (a real create never has initialData, and a
         * real edit is always mode='edit'). Duplicate should load
         * everything else as-is so the user only has to change
         * what's different - but the slug has a unique index on
         * the backend, and buildAccommodationPayload() reuses
         * formData.slug verbatim when it's non-empty. Carrying
         * the original slug forward would submit the exact same
         * slug as a second listing and fail on save. Clearing it
         * here lets the backend auto-generate a fresh one (or the
         * user can type their own on the Basic Information step).
         */
        if (mode === 'create' && initialData) {
            mergedData.slug = '';
        }

        /*
         * DUPLICATE: drop every saved id (units, photos, rate plans, ...).
         * Units keep their ids on edit so bookings and room counts stay linked —
         * a copy must get new ones, or both listings would share the same rooms.
         */
        const stripIds = (value) => {
            if (Array.isArray(value)) return value.map(stripIds);
            if (value && typeof value === 'object' && !(value instanceof File) && !(value instanceof Blob)) {
                return Object.fromEntries(
                    Object.entries(value)
                        .filter(([key]) => key !== '_id')
                        .map(([key, item]) => [key, stripIds(item)])
                );
            }
            return value;
        };

        setFormData(mode === 'create' ? stripIds(mergedData) : mergedData);

        /*
         * Edit mode should not restore an old create draft -
         * but it also shouldn't make every step look untouched.
         * The record being edited already has real data in it,
         * so mark whichever steps already pass validation as
         * completed up front (green tick), instead of forcing
         * the organizer/support user to click Continue through
         * all 10 steps just to get the sidebar to agree that
         * data which was already there is actually there.
         * 'review' is left out - it has no fields of its own,
         * so it only completes when the form is submitted.
         */
        const { errors: stepErrors } = validateAllStepsData(mergedData, steps);

        setCompletedSteps(steps.map((step) => step.id).filter((stepId) => stepId !== 'review' && !stepErrors[stepId]));

        setCurrentStep(0);

        setErrors({});
    }, [initialData]);

    // =====================================================
    // LOCAL STORAGE RECOVERY
    // =====================================================

    useEffect(() => {
        /*
         * NEVER restore localStorage draft in edit mode.
         */
        if (mode !== 'create' || initialData) {
            draftInitialized.current = true;
            return;
        }

        try {
            const savedDraft = localStorage.getItem(DRAFT_KEY);

            if (!savedDraft) {
                draftInitialized.current = true;
                return;
            }

            const parsedDraft = JSON.parse(savedDraft);

            const restoredData = {
                ...initialFormData,
                ...parsedDraft,

                address: {
                    ...initialFormData.address,
                    ...(parsedDraft.address || {}),

                    geo: {
                        ...initialFormData.address.geo,
                        ...(parsedDraft.address?.geo || {})
                    }
                },

                amenities: {
                    ...initialFormData.amenities,
                    ...(parsedDraft.amenities || {})
                },

                policies: {
                    ...initialFormData.policies,
                    ...(parsedDraft.policies || {}),

                    cancellation: {
                        ...initialFormData.policies.cancellation,
                        ...(parsedDraft.policies?.cancellation || {})
                    }
                },

                bookingSettings: {
                    ...initialFormData.bookingSettings,
                    ...(parsedDraft.bookingSettings || {})
                },

                paymentOptions: {
                    ...initialFormData.paymentOptions,
                    ...(parsedDraft.paymentOptions || {})
                },

                seo: {
                    ...initialFormData.seo,
                    ...(parsedDraft.seo || {})
                },

                /*
                 * Defensive: drafts saved before the backend<->form
                 * reverse-mapper existed can still be sitting in a
                 * browser's localStorage with raw backend shapes
                 * (e.g. bathrooms as {count,type,hotWater} instead
                 * of a plain number). Restoring one of those as-is
                 * crashes UnitCard/UnitEditor, which only ever
                 * render/edit bathrooms as a number. Coerce on the
                 * way back in so an old draft can't do that.
                 */
                units: Array.isArray(parsedDraft.units)
                    ? parsedDraft.units.map((unit) => ({
                          ...unit,

                          bathrooms:
                              unit && isObject(unit.bathrooms)
                                  ? toNumber(unit.bathrooms.count, 1)
                                  : toNumber(unit?.bathrooms, 1)
                      }))
                    : [],

                media: normalizeMedia(parsedDraft.media),

                cardImages: Array.isArray(parsedDraft.cardImages) ? parsedDraft.cardImages : [],

                videos: Array.isArray(parsedDraft.videos) ? parsedDraft.videos : []
            };

            setFormData(restoredData);

            if (
                typeof parsedDraft.currentStep === 'number' &&
                parsedDraft.currentStep >= 0 &&
                parsedDraft.currentStep < steps.length
            ) {
                setCurrentStep(parsedDraft.currentStep);
            }

            if (Array.isArray(parsedDraft.completedSteps)) {
                setCompletedSteps(parsedDraft.completedSteps);
            }
        } catch (error) {
            console.error('Failed to restore accommodation draft:', error);
        } finally {
            draftInitialized.current = true;
        }
    }, [mode, initialData]);

    // =====================================================
    // AUTO SAVE
    // =====================================================

    useEffect(() => {
        if (!draftInitialized.current) {
            return;
        }

        /*
         * Auto recovery is only for create mode.
         *
         * Edit data should come from API/database.
         */
        if (mode !== 'create' || initialData) {
            return;
        }

        const timer = setTimeout(() => {
            try {
                const draft = {
                    ...formData,

                    currentStep,

                    completedSteps,

                    units: (formData.units || []).map((unit) => ({
                        ...unit,

                        images: (unit.images || []).map(cleanFileObject).filter(Boolean)
                    })),

                    media: normalizeMedia(formData.media),

                    cardImages: (formData.cardImages || []).map(cleanFileObject).filter(Boolean),

                    videos: Array.isArray(formData.videos) ? formData.videos.map(cleanFileObject).filter(Boolean) : []
                };

                localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
            } catch (error) {
                console.error('Failed to auto-save accommodation:', error);
            }
        }, 5000);

        return () => clearTimeout(timer);
    }, [formData, currentStep, completedSteps, mode, initialData]);

    // =====================================================
    // SCROLL
    // =====================================================

    const scrollToTop = () => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    };

    // =====================================================
    // FIELD UPDATE
    // =====================================================

    const updateField = (field, value) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value
        }));
    };

    // =====================================================
    // NESTED FIELD UPDATE
    // =====================================================

    const updateNestedField = (parent, field, value) => {
        setFormData((prev) => ({
            ...prev,

            [parent]: {
                ...(prev[parent] || {}),
                [field]: value
            }
        }));
    };

    // =====================================================
    // ADDRESS UPDATE
    // =====================================================

    const updateAddress = (field, value) => {
        setFormData((prev) => ({
            ...prev,

            address: {
                ...prev.address,
                [field]: value
            }
        }));
    };

    // =====================================================
    // VALIDATE STEP
    // (implementation lives in accommodationValidation.js)
    // =====================================================

    const validateStep = (stepIndex, data = formData) => {
        return validateStepData(stepIndex, data, steps);
    };

    // =====================================================
    // VALIDATE CURRENT STEP
    // =====================================================

    const validateCurrentStep = () => {
        const newErrors = validateStep(currentStep);

        setErrors(newErrors);

        return Object.keys(newErrors).length === 0;
    };

    // =====================================================
    // MARK COMPLETED
    // =====================================================

    const markStepCompleted = (stepId) => {
        setCompletedSteps((previous) => {
            if (previous.includes(stepId)) {
                return previous;
            }

            return [...previous, stepId];
        });
    };

    // =====================================================
    // NEXT
    // =====================================================

    const handleNext = () => {
        const valid = validateCurrentStep();

        if (!valid) {
            scrollToTop();
            return;
        }

        const stepId = steps[currentStep].id;

        markStepCompleted(stepId);

        if (currentStep < steps.length - 1) {
            setCurrentStep((previous) => previous + 1);

            setErrors({});

            scrollToTop();
        }
    };

    // =====================================================
    // BACK
    // =====================================================

    const handleBack = () => {
        if (currentStep === 0) {
            return;
        }

        setCurrentStep((previous) => previous - 1);

        setErrors({});

        scrollToTop();
    };

    // =====================================================
    // STEP CLICK
    // =====================================================

    const handleStepClick = (index) => {
        if (index < 0 || index >= steps.length) {
            return;
        }

        if (index === currentStep) {
            return;
        }

        /*
         * User can freely move between sections.
         *
         * We do not validate here.
         *
         * Validation happens when Continue or Submit
         * is clicked.
         */

        setCurrentStep(index);

        setErrors({});

        scrollToTop();
    };

    // =====================================================
    // FINAL VALIDATION
    // =====================================================

    const validateAllSteps = () => {
        return validateAllStepsData(formData, steps);
    };

    // =====================================================
    // SUBMIT
    // =====================================================

    const handleSubmit = async () => {
        // ---------------------------------------------
        // FINAL VALIDATION
        // ---------------------------------------------

        const validation = validateAllSteps();

        if (!validation.valid) {
            const firstInvalidStep = validation.firstInvalidStep;

            if (firstInvalidStep !== null) {
                setCurrentStep(firstInvalidStep);

                setErrors(validation.errors[steps[firstInvalidStep].id] || {});

                scrollToTop();
            }

            toast.error('Please complete the required fields before submitting.');

            return;
        }

        // ---------------------------------------------
        // API
        // ---------------------------------------------

        try {
            setSaving(true);

            /*
             * MEDIA UPLOAD:
             *
             * formData.media / cardImages / videos / units[].images
             * can still hold raw File objects for anything the
             * organizer selected but never uploaded. Upload those
             * to Firebase Storage first and swap them for their
             * download URLs - otherwise the payload builder has
             * no url to send and silently drops the image.
             */

            const uploadToast = toast.loading('Uploading photos and videos...');

            let formDataWithUploadedMedia;

            try {
                formDataWithUploadedMedia = await uploadAccommodationMedia(formData);

                // Keep local state in sync with the uploaded URLs so a
                // re-submit (or edit mode) doesn't try to re-upload the
                // same files again.
                setFormData(formDataWithUploadedMedia);

                toast.update(uploadToast, {
                    render: 'Media uploaded.',
                    type: 'success',
                    isLoading: false,
                    autoClose: 1200
                });
            } catch (uploadError) {
                console.error('Failed to upload accommodation media:', uploadError);

                toast.update(uploadToast, {
                    render: 'Failed to upload one or more photos/videos. Please try again.',
                    type: 'error',
                    isLoading: false,
                    autoClose: 4000
                });

                setSaving(false);
                return;
            }

            /*
             * IMPORTANT:
             *
             * buildAccommodationPayload intentionally
             * removes:
             *
             * - organizerId
             * - listingType
             * - status
             *
             * Backend will decide these values.
             */

            const payload = buildAccommodationPayload(formDataWithUploadedMedia);

            console.log('FINAL ACCOMMODATION PAYLOAD:', payload);

            let response;

            // =========================================
            // CREATE
            // =========================================

            if (!accommodationId) {
                if (role === 'support') {
                    /*
                     * Support-side create goes through the shared
                     * org-pack admin endpoint (type='camp'), which
                     * needs an explicit organizerId in the body -
                     * the backend can't infer it from a support
                     * user's own token the way it does for an
                     * organizer. admOrgTripCre also activates the
                     * listing immediately (no review step) when
                     * support creates it.
                     */
                    response = await supTripCreateFun('camp', { ...payload, organizerId: orgId });

                    console.log('Accommodation created (support):', response);

                    toast.success('Accommodation created successfully.');
                } else {
                    response = await orgCreateAccommodation(payload);

                    console.log('Accommodation created:', response);

                    /*
                     * Organizer-created accommodation should
                     * become PENDING on backend.
                     *
                     * Do not send status from frontend.
                     */

                    toast.success('Accommodation submitted successfully for review.');
                }
            }

            // =========================================
            // EDIT
            // =========================================
            else {
                if (role === 'support') {
                    /*
                     * admOrgTripEdit reads the record id from
                     * req.body._id (not a URL param), and doesn't
                     * scope by organizer - support can edit any
                     * org's accommodation this way.
                     */
                    response = await supTripEdit('camp', { ...payload, _id: accommodationId });

                    console.log('Accommodation updated (support):', response);
                } else {
                    response = await orgUpdateAccommodation(accommodationId, payload);

                    console.log('Accommodation updated:', response);
                }

                toast.success('Accommodation updated successfully.');
            }

            // -----------------------------------------
            // CLEAR RECOVERY DRAFT
            // -----------------------------------------

            try {
                localStorage.removeItem(DRAFT_KEY);
            } catch (storageError) {
                console.error('Failed to clear accommodation draft:', storageError);
            }

            onSuccess?.(response);

            return response;
        } catch (error) {
            console.error('Failed to submit accommodation:', error);

            const message =
                error?.response?.data?.message ||
                error?.response?.data?.error ||
                error?.message ||
                'Failed to submit accommodation';

            toast.error(message);
        } finally {
            setSaving(false);
        }
    };

    // =====================================================
    // RENDER CURRENT STEP
    // =====================================================

    const renderCurrentStep = () => {
        const stepId = steps[currentStep]?.id;

        switch (stepId) {
            // ==========================================
            // BASIC
            // ==========================================

            case 'basic':
                return (
                    <BasicInformation
                        formData={formData}
                        updateField={updateField}
                        updateNestedField={updateNestedField}
                        errors={errors}
                        canEdit={canEdit('basic')}
                        mode={mode}
                        role={role}
                    />
                );

            // ==========================================
            // LOCATION
            // ==========================================

            case 'location':
                return (
                    <LocationSection
                        formData={formData}
                        updateAddress={updateAddress}
                        errors={errors}
                        canEdit={canEdit('location')}
                        mode={mode}
                        role={role}
                    />
                );

            // ==========================================
            // UNITS
            // ==========================================

            case 'units':
                return (
                    <AccommodationUnits
                        formData={formData}
                        updateField={updateField}
                        errors={errors}
                        canEdit={canEdit('units')}
                    />
                );

            // ==========================================
            // AMENITIES
            // ==========================================

            case 'amenities':
                return (
                    <AmenitiesSection
                        formData={formData}
                        updateField={updateField}
                        errors={errors}
                        canEdit={canEdit('amenities')}
                    />
                );

            // ==========================================
            // PRICING
            // ==========================================

            case 'pricing':
                return (
                    <PricingSection
                        formData={formData}
                        updateField={updateField}
                        errors={errors}
                        canEdit={canEdit('pricing')}
                    />
                );

            // ==========================================
            // SERVICES
            // ==========================================

            case 'services':
                return (
                    <ServicesSection
                        formData={formData}
                        updateField={updateField}
                        errors={errors}
                        canEdit={canEdit('services')}
                    />
                );

            // ==========================================
            // POLICIES
            // ==========================================

            case 'policies':
                return (
                    <PoliciesSection
                        formData={formData}
                        updateField={updateField}
                        errors={errors}
                        canEdit={canEdit('policies')}
                    />
                );

            // ==========================================
            // MEDIA
            // ==========================================

            case 'media':
                return (
                    <MediaSection
                        formData={formData}
                        updateField={updateField}
                        errors={errors}
                        canEdit={canEdit('media')}
                    />
                );

            // ==========================================
            // ADDITIONAL
            // ==========================================

            case 'additional':
                return (
                    <AdditionalInformation
                        formData={formData}
                        updateField={updateField}
                        errors={errors}
                        canEdit={canEdit('additional')}
                    />
                );

            // ==========================================
            // REVIEW
            // ==========================================

            case 'review':
                return (
                    <ReviewSubmit
                        formData={formData}
                        completedSteps={completedSteps}
                        steps={steps}
                        onEditStep={handleStepClick}
                        onSubmit={handleSubmit}
                        saving={saving}
                    />
                );

            default:
                return null;
        }
    };

    // =====================================================
    // RENDER
    // =====================================================

    return (
        <div className='accommodation-form'>
            {/* =================================================
                HEADER
            ================================================= */}

            <div className='accommodation-header'>
                <div>
                    <h1>{mode === 'edit' ? 'Edit Accommodation' : 'Create Accommodation'}</h1>

                    <p>
                        {mode === 'edit' ? 'Update your accommodation details.' : 'Add a new accommodation property.'}
                    </p>
                </div>
            </div>

            {/* =================================================
                MAIN LAYOUT
            ================================================= */}

            <div className='accommodation-layout'>
                {/* =================================================
                    SIDEBAR
                ================================================= */}

                <aside className='accommodation-sidebar'>
                    <div className='sidebar-title'>List Your Property</div>

                    <div className='steps-list'>
                        {steps.map((step, index) => {
                            const isActive = currentStep === index;

                            const isCompleted = completedSteps.includes(step.id);

                            return (
                                <button
                                    key={step.id}
                                    type='button'
                                    className={`
                                            step-item
                                            ${isActive ? 'active' : ''}
                                            ${isCompleted ? 'completed' : ''}
                                        `}
                                    onClick={() => handleStepClick(index)}
                                >
                                    <span className='step-number'>{isCompleted ? '✓' : index + 1}</span>

                                    <span className='step-content'>
                                        <strong>{step.label}</strong>

                                        <small>{step.description}</small>
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </aside>

                {/* =================================================
                    CONTENT
                ================================================= */}

                <main className='accommodation-content'>
                    {/* =================================================
                        STEP HEADER
                    ================================================= */}

                    <div className='content-header'>
                        <div>
                            <span className='step-counter'>
                                Step {currentStep + 1} of {steps.length}
                            </span>

                            <h2>{steps[currentStep].label}</h2>

                            <p>{steps[currentStep].description}</p>
                        </div>
                    </div>

                    {/* =================================================
                        VALIDATION SUMMARY
                    ================================================= */}

                    {Object.keys(errors).length > 0 && (
                        <div className='form-error-summary'>
                            <strong>Please fix the highlighted fields.</strong>

                            {errors.units && <p>{errors.units}</p>}

                            {errors.pricing && <p>{errors.pricing}</p>}

                            {errors.services && <p>{errors.services}</p>}
                        </div>
                    )}

                    {/* =================================================
                        FORM CARD
                    ================================================= */}

                    <div className='form-card'>{renderCurrentStep()}</div>

                    {/* =================================================
                        FOOTER
                    ================================================= */}

                    <div className='form-footer'>
                        <button
                            type='button'
                            className='btn btn-secondary'
                            onClick={handleBack}
                            disabled={currentStep === 0 || saving}
                        >
                            Back
                        </button>

                        <div className='footer-right'>
                            {/* jump straight to Review & Submit (e.g. after changing one thing on edit) */}
                            {currentStep < steps.length - 1 && (
                                <button
                                    type='button'
                                    className='btn btn-secondary'
                                    onClick={() => handleStepClick(steps.length - 1)}
                                    disabled={saving}
                                >
                                    Go to Review
                                </button>
                            )}

                            {currentStep < steps.length - 1 ? (
                                <button
                                    type='button'
                                    className='btn btn-primary'
                                    onClick={handleNext}
                                    disabled={saving}
                                >
                                    Continue
                                    <span>→</span>
                                </button>
                            ) : (
                                <button
                                    type='button'
                                    className='btn btn-primary'
                                    onClick={handleSubmit}
                                    disabled={saving}
                                >
                                    {saving ? 'Submitting...' : mode === 'edit' ? 'Update Property' : 'Submit Property'}
                                </button>
                            )}
                        </div>
                    </div>
                </main>
            </div>
        </div>
    );
};

export default AccommodationForm;
